import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { getApiUrl } from '../methods/helpers';
import { getLicense } from '../methods/license';
import { AppConfigCacheService, CachedAppConfig } from './app-config-cache.service';

const defaultDesign = require('../../../resources/designs/default.json');

export type AppConfigLoadStatus = 'loaded' | 'unchanged' | 'cacheFailed' | 'fetchFailed' | 'invalidResponse';

type FetchResult = Partial<CachedAppConfig> & { didFetch: boolean; didReach: boolean; invalidResponse: boolean };

@Injectable({ providedIn: 'root' })
export class AppConfigService {
  lastStatus: AppConfigLoadStatus = 'unchanged';
  private loadSequence = 0;
  private tracingAnnounced = false;

  constructor(private cache: AppConfigCacheService) {}

  async load(configName?: string, reset = false, forceLicense = false): Promise<boolean> {
    const serial = localStorage.getItem('serial');
    const traceId = `cfg-${Date.now().toString(36)}-${++this.loadSequence}`;
    const startedAt = Date.now();
    let current: CachedAppConfig = this.defaultConfig();
    let didFetch = false;
    let didReach = false;
    let invalidResponse = false;
    this.trace(traceId, 'load.start', () => ({
      source: configName ? 'designTag' : serial ? 'serial' : 'cachedDesignTag',
      designTag: configName ?? undefined,
      serial: this.maskSerial(serial),
      reset,
      forceLicense,
      ...this.localStorageUsage(),
    }));

    if (reset) {
      try {
        this.trace(traceId, 'cache.clear.start');
        await this.cache.clear();
        this.trace(traceId, 'cache.clear.success');
      } catch (error) {
        this.logCacheError(traceId, 'clear', error, undefined);
      }
    } else {
      try {
        this.trace(traceId, 'cache.read.start');
        current = (await this.cache.readOrMigrateLegacy()) ?? current;
        const cacheDriver = environment.configDebug === true ? await this.cache.driver() : undefined;
        this.trace(traceId, 'cache.read.success', () => ({
          source: this.cache.lastReadSource,
          driver: cacheDriver,
          configTimestamp: current.timestamp,
          designTag: current.designName || undefined,
          bytes: this.configBytes(current),
          ...this.localStorageUsage(),
        }));
      } catch (error) {
        this.logCacheError(traceId, 'read', error, undefined);
      }

      const retries = current.settings?.offlineConfig ? 1 : 6;
      let response: FetchResult | undefined;
      if (configName) {
        response = await this.fetchConfig('designByTag', configName, current.timestamp, retries, traceId);
      } else if (serial) {
        response = await this.fetchConfig('designBySerial', serial, current.timestamp, retries, traceId);
      } else if (current.designName) {
        response = await this.fetchConfig('designByTag', current.designName, current.timestamp, current.settings?.offlineConfig ? 1 : 3, traceId);
      }

      if (response) {
        didFetch = response.didFetch;
        didReach = response.didReach;
        invalidResponse = response.invalidResponse;
        if (response.didFetch) {
          current = response as CachedAppConfig;
        }
      }
    }

    if (!current.settings.homeStage) {
      current.settings.homeStage = current.settings.initialStage;
    }
    this.activate(current);
    this.trace(traceId, 'config.activated', () => ({
      designTag: current.designName || undefined,
      configTimestamp: current.timestamp,
      bytes: this.configBytes(current),
    }));

    if (didFetch) {
      try {
        this.trace(traceId, 'cache.write.start', () => ({ bytes: this.configBytes(current) }));
        await this.cache.write(current);
        this.lastStatus = 'loaded';
        const cacheDriver = environment.configDebug === true ? await this.cache.driver() : undefined;
        this.trace(traceId, 'cache.write.success', { driver: cacheDriver });
      } catch (error) {
        this.lastStatus = 'cacheFailed';
        this.logCacheError(traceId, 'write', error, current);
      }
      this.setTargetApi(current.settings);
    } else if (invalidResponse) {
      this.lastStatus = 'invalidResponse';
    } else if (!didReach && (configName || serial || current.designName)) {
      this.lastStatus = 'fetchFailed';
    } else {
      this.lastStatus = 'unchanged';
    }

    const lastLanguage = localStorage.getItem('LOADED_LANGUAGE');
    const currentLanguage = localStorage.getItem('SELECTED_LANGUAGE');
    const reload = lastLanguage !== currentLanguage
      ? true
      : (configName !== undefined ? didReach : didFetch);
    if (lastLanguage !== currentLanguage) {
      localStorage.setItem('LOADED_LANGUAGE', currentLanguage ?? '');
    }
    if (serial) {
      try {
        this.trace(traceId, 'license.refresh.start', { serial: this.maskSerial(serial), reload, forceLicense });
        await getLicense(serial, reload || forceLicense);
        this.trace(traceId, 'license.refresh.success');
      } catch (error) {
        this.traceError(traceId, 'license.refresh.failed', error);
      }
    }
    this.trace(traceId, 'load.complete', () => ({
      status: this.lastStatus,
      didFetch,
      didReach,
      invalidResponse,
      reload,
      durationMs: Date.now() - startedAt,
      ...this.localStorageUsage(),
    }));
    return reload;
  }

  private async fetchConfig(kind: 'designBySerial' | 'designByTag', value: string, timestamp: any, retries: number, traceId?: string): Promise<FetchResult> {
    const result: FetchResult = { didFetch: false, didReach: false, invalidResponse: false };
    for (let index = 0; index < retries; index++) {
      const url = `${getApiUrl(index > 0)}/config/${kind}/${value}${timestamp ? `?timestamp=${timestamp}` : ''}`;
      const timeoutMs = kind === 'designBySerial' && index >= 3 ? 60000 : 5000;
      this.trace(traceId, 'fetch.attempt', {
        attempt: index + 1,
        retries,
        kind,
        host: new URL(url).host,
        timeoutMs,
      });
      try {
        const attemptStartedAt = Date.now();
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Access-Control-Allow-Origin': '*',
            Accept: 'application/json; charset=UTF-8',
            'Content-Type': 'application/json',
            Authorization: 'Basic ' + btoa(`${environment.config.updateUser}:${environment.config.updatePassword}`),
          },
          // @ts-ignore AbortSignal.timeout is available in the supported WebViews.
          signal: AbortSignal.timeout(timeoutMs),
        });
        this.trace(traceId, 'fetch.response', {
          attempt: index + 1,
          status: response.status,
          durationMs: Date.now() - attemptStartedAt,
        });
        if (response.status === 200) {
          result.didReach = true;
          const data = await response.json();
          if (!data?.timestamp) {
            if (timestamp) {
              this.trace(traceId, 'fetch.unchanged', { configTimestamp: timestamp });
              return result;
            }
            result.invalidResponse = true;
            this.trace(traceId, 'fetch.invalidResponse', { reason: 'missing timestamp' });
            return result;
          }
          result.configs = data.configs;
          result.settings = data.settings;
          result.languages = data.language ?? {};
          result.templates = data.templates ?? {};
          result.templatesElements = data.templatesElements ?? {};
          result.timestamp = data.timestamp;
          result.designName = data.designTag ?? (kind === 'designByTag' ? value : '');
          result.didFetch = !!result.configs && !!result.settings;
          result.invalidResponse = !result.didFetch;
          this.trace(traceId, result.didFetch ? 'fetch.validPayload' : 'fetch.invalidResponse', () => ({
            designTag: result.designName || undefined,
            configTimestamp: result.timestamp,
            bytes: result.didFetch ? this.configBytes(result as CachedAppConfig) : 0,
            reason: result.didFetch ? undefined : 'missing configs or settings',
          }));
          return result;
        }
      } catch (error) {
        this.traceError(traceId, 'fetch.failed', error, { attempt: index + 1, host: new URL(url).host });
      }
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    return result;
  }

  private defaultConfig(): CachedAppConfig {
    return {
      configs: defaultDesign.configs,
      settings: { ...defaultDesign.settings },
      languages: defaultDesign.language ?? {},
      templates: defaultDesign.templates ?? {},
      templatesElements: defaultDesign.templatesElements ?? {},
      timestamp: 0,
      designName: '',
    };
  }

  private activate(config: CachedAppConfig): void {
    environment.appDesignConfigs = config.configs;
    environment.appDesignSettings = config.settings;
    environment.appDesignLanguages = config.languages;
    environment.appDesignTemplates = config.templates ?? {};
    environment.appDesignTemplatesElements = config.templatesElements ?? {};
    environment.appDesignName = config.designName;
  }

  private setTargetApi(settings: any): void {
    if (!settings?.targetAPI) {
      return;
    }
    const index = environment.appConfigUrl.indexOf(`https://${settings.targetAPI}`);
    document.cookie = `apiUrlIndex=${index >= 0 ? index : 0}`;
  }

  private logCacheError(traceId: string, operation: string, error: any, value: CachedAppConfig | undefined): void {
    this.traceError(traceId, 'cache.failed', error, {
      operation,
      key: 'app-config-cache-v1',
      bytes: this.configBytes(value),
    });
  }

  private trace(
    traceId: string | undefined,
    phase: string,
    details: Record<string, any> | (() => Record<string, any>) = {},
  ): void {
    if (environment.configDebug !== true) {
      return;
    }
    const resolvedDetails = typeof details === 'function' ? details() : details;
    const entry = { type: 'config', traceId, phase, timestamp: Date.now(), ...resolvedDetails };
    this.announceTracing();
    console.log(this.formatTrace(entry));
  }

  private traceError(traceId: string | undefined, phase: string, error: any, details: Record<string, any> = {}): void {
    const entry = {
      type: 'config-error', traceId, phase, timestamp: Date.now(), ...details,
      errorName: error?.name,
      errorMessage: error?.message ?? String(error),
      errorStack: error?.stack,
    };
    console.error(this.formatTrace(entry));
  }

  private announceTracing(): void {
    if (this.tracingAnnounced) {
      return;
    }
    this.tracingAnnounced = true;
    console.log('[AppConfig] tracing enabled');
  }

  /**
   * Cordova's iOS console bridge converts a second object argument to
   * "[object Object]". Emit one JSON string so Xcode retains every field.
   */
  private formatTrace(entry: Record<string, any>): string {
    try {
      return `[AppConfig] ${JSON.stringify(entry)}`;
    } catch (error) {
      return `[AppConfig] {"type":"config-log-format-error","phase":${JSON.stringify(entry.phase)},"errorMessage":${JSON.stringify(error?.message ?? String(error))}}`;
    }
  }

  private configBytes(value: CachedAppConfig | undefined): number {
    try {
      return value ? new Blob([JSON.stringify(value)]).size : 0;
    } catch (_) {
      return 0;
    }
  }

  private maskSerial(serial: string | null): string | undefined {
    if (!serial) {
      return undefined;
    }
    return serial.length <= 4 ? serial : `…${serial.slice(-4)}`;
  }

  /**
   * Measures the actual Web Storage payload using DOMString/UTF-16 byte length.
   * This is diagnostic usage only; it is not the quota granted by WebKit.
   */
  private localStorageUsage(): Record<string, number | string | null> {
    try {
      let bytes = 0;
      for (let index = 0; index < localStorage.length; index++) {
        const key = localStorage.key(index) ?? '';
        const value = localStorage.getItem(key) ?? '';
        bytes += (key.length + value.length) * 2;
      }
      return { localStorageBytes: bytes, localStorageKeys: localStorage.length };
    } catch (error) {
      return {
        localStorageBytes: null,
        localStorageKeys: null,
        localStorageMeasurementError: error?.message ?? String(error),
      };
    }
  }
}
